// using copilot-instructions: Unified Zustand store for all workspace layout state (panels, sidebars)
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { getWorkspaceLayoutKey } from "../workspace/constants.js";

/**
 * User/broker context for scoped workspace storage
 * Set by authStore after successful login
 */
let currentUser = null;
let currentBroker = null;

/**
 * Set the current user/broker context for workspace storage scoping
 * Called by authStore after successful authentication
 * @param {string} user - Username/email
 * @param {string} broker - Broker identifier
 */
export const setWorkspaceContext = (user, broker) => {
  currentUser = user;
  currentBroker = broker;
  console.log(`[WorkspaceStore] Context set to: ${broker}/${user}`);

  // Trigger rehydration of all scoped stores after context is set
  // This ensures they load the correct user/broker data
  if (typeof window !== "undefined") {
    // Dispatch custom event to notify stores to rehydrate
    window.dispatchEvent(
      new CustomEvent("workspace-context-changed", {
        detail: { user, broker },
      }),
    );
  }
};

/**
 * Get current workspace context
 * @returns {{user: string|null, broker: string|null}}
 */
export const getWorkspaceContext = () => ({
  user: currentUser,
  broker: currentBroker,
});

/**
 * Create custom scoped storage adapter for any key generator function
 * @param {Function} keyGenerator - Function that takes (user, broker) and returns a key
 * @returns {Object} - Storage adapter for Zustand persist middleware
 */
export const createScopedStorage = (keyGenerator) => ({
  getItem: (name) => {
    const key = keyGenerator(currentUser, currentBroker);
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  },
  setItem: (name, value) => {
    const key = keyGenerator(currentUser, currentBroker);
    localStorage.setItem(key, JSON.stringify(value));
  },
  removeItem: (name) => {
    const key = keyGenerator(currentUser, currentBroker);
    localStorage.removeItem(key);
  },
});

/**
 * Get a scoped localStorage value (for non-Zustand direct localStorage access)
 * @param {Function} keyGenerator - Function that takes (user, broker) and returns a key
 * @returns {string|null} - Value from localStorage
 */
export const getScopedItem = (keyGenerator) => {
  const key = keyGenerator(currentUser, currentBroker);
  return localStorage.getItem(key);
};

/**
 * Set a scoped localStorage value (for non-Zustand direct localStorage access)
 * @param {Function} keyGenerator - Function that takes (user, broker) and returns a key
 * @param {string} value - Value to store
 */
export const setScopedItem = (keyGenerator, value) => {
  const key = keyGenerator(currentUser, currentBroker);
  localStorage.setItem(key, value);
};

/**
 * Remove a scoped localStorage value (for non-Zustand direct localStorage access)
 * @param {Function} keyGenerator - Function that takes (user, broker) and returns a key
 */
export const removeScopedItem = (keyGenerator) => {
  const key = keyGenerator(currentUser, currentBroker);
  localStorage.removeItem(key);
};

const useWorkspaceStore = create(
  persist(
    (set, get) => ({
      // Router Operations Sidebar (right sidebar)
      isRouterOperationsSidebarOpen: false,
      toggleRouterOperationsSidebar: () =>
        set((state) => ({
          isRouterOperationsSidebarOpen: !state.isRouterOperationsSidebarOpen,
        })),
      setRouterOperationsSidebarOpen: (open) =>
        set({ isRouterOperationsSidebarOpen: open }),

      // Routing Menu
      isRoutingMenuOpen: false,
      toggleRoutingMenu: () =>
        set((state) => ({ isRoutingMenuOpen: !state.isRoutingMenuOpen })),
      setRoutingMenuOpen: (open) => set({ isRoutingMenuOpen: open }),

      // Desktop Name
      desktopName: "Novo desktop",
      setDesktopName: (name) => set({ desktopName: name }),

      // Current desktop id (server chartid) — persisted so reopening the app
      // returns to the same desktop. null = unsaved "Novo desktop".
      currentDesktopId: null,
      setCurrentDesktopId: (id) => set({ currentDesktopId: id }),

      // Indicators Dropdown
      isIndicatorsDropdownOpen: false,
      toggleIndicatorsDropdown: () =>
        set((state) => ({
          isIndicatorsDropdownOpen: !state.isIndicatorsDropdownOpen,
        })),
      setIndicatorsDropdownOpen: (open) =>
        set({ isIndicatorsDropdownOpen: open }),

      // Measure Mode (Issue #101)
      isMeasureModeActive: false,
      toggleMeasureMode: () =>
        set((state) => ({ isMeasureModeActive: !state.isMeasureModeActive })),
      setMeasureModeActive: (active) => set({ isMeasureModeActive: active }),

      // Dock layout
      dockLayout: null, // FlexLayout Model JSON, null if never used dock
      setDockLayout: (layout) => set({ dockLayout: layout }),
      dockLayoutMode: "auto", // 'auto' | 'custom'
      setDockLayoutMode: (mode) => set({ dockLayoutMode: mode }),

      // Chart add mode: 'tabs' | 'grid' | 'smart'
      chartAddMode: "tabs",
      setChartAddMode: (mode) => set({ chartAddMode: mode }),

      // Dock panel tracking (not persisted — derived from dockLayout on mount)
      dockPanels: {
        positions: false,
        openOrders: false,
        log: false,
        grid: false,
        info: false,
      },
      setDockPanelOpen: (panelType, isOpen) =>
        set((state) => ({
          dockPanels: { ...state.dockPanels, [panelType]: isOpen },
        })),

      // One-shot: pedido de injetar o painel Info ao montar o layout de um desktop
      // importado do Silverlight (consumido uma vez pelo efeito requestOrganize do
      // DockModeLayout, que doca o Info à direita se não existir). Transitório.
      pendingInfoInject: false,
      setPendingInfoInject: (v) => set({ pendingInfoInject: v }),

      // Bridge function: DockModeLayout registers its addPanelTab here
      _addDockPanelFn: null,
      setAddDockPanelFn: (fn) => set({ _addDockPanelFn: fn }),
      addDockPanel: (panelType) => {
        get()._addDockPanelFn?.(panelType);
      },

      // Bridge: DockModeLayout registers a fresh model.toJson() getter here.
      // Moving/resizing a native popout window does NOT fire onModelChange, so
      // the persisted dockLayout goes stale — snapshots must read the LIVE
      // model (its toJson() re-reads each popout window's screen rect).
      _getDockLayoutFn: null,
      setGetDockLayoutFn: (fn) => set({ _getDockLayoutFn: fn }),
      getLiveDockLayout: () => {
        try {
          return get()._getDockLayoutFn?.() ?? get().dockLayout;
        } catch (error) {
          console.error("[WorkspaceStore] Live dockLayout read failed:", error);
          return get().dockLayout;
        }
      },

      // Focused panel type — determines spotlight behavior (non-persisted, ephemeral)
      focusedPanelType: "chart",
      setFocusedPanelType: (type) => set({ focusedPanelType: type }),

      // Legacy panel hints — ephemeral, used once by DockModeLayout when migrating old desktops
      _legacyPanelHints: null,

      // One-shot layout reorganization request (not persisted)
      requestOrganize: null, // 'grid' | 'tabs' | null
      setRequestOrganize: (action) => set({ requestOrganize: action }),

      // Non-persisted key incremented on desktop restore to force DockModeLayout remount
      dockLayoutKey: 0,
      incrementDockLayoutKey: () =>
        set((state) => ({ dockLayoutKey: state.dockLayoutKey + 1 })),
    }),
    {
      name: "workspace-layout", // Used as fallback name, actual key is dynamic
      storage: createJSONStorage(() =>
        createScopedStorage(getWorkspaceLayoutKey),
      ),
      partialize: (state) => {
        // Exclude non-persisted state
        const {
          dockPanels,
          _addDockPanelFn,
          setAddDockPanelFn,
          addDockPanel,
          _getDockLayoutFn,
          setGetDockLayoutFn,
          getLiveDockLayout,
          setDockPanelOpen,
          dockLayoutKey,
          incrementDockLayoutKey,
          focusedPanelType,
          setFocusedPanelType,
          _legacyPanelHints,
          requestOrganize,
          setRequestOrganize,
          ...persisted
        } = state;
        return persisted;
      },
    },
  ),
);

// Listen for workspace context changes and rehydrate the store
// This ensures dockLayout and other settings are loaded from the user-scoped key after login
if (typeof window !== "undefined") {
  window.addEventListener("workspace-context-changed", () => {
    useWorkspaceStore.persist.rehydrate();
  });
}

// Expose store on window for E2E tests and dev tools (mirrors __CHARTS_STORE__ pattern)
if (typeof window !== "undefined") {
  window.__WORKSPACE_STORE__ = useWorkspaceStore.getState();
  useWorkspaceStore.subscribe((state) => {
    window.__WORKSPACE_STORE__ = state;
  });
}

export default useWorkspaceStore;
